/**
 * Telemetry Synchronization Stage
 *
 * Synchronizes all telemetry data to a common time interval using interpolation
 */

import {
  PipelineStage,
  PipelineContext,
  StageResult,
} from '../ProcessingPipeline'
import { ProcessedTelemetryData } from '../../../domain/telemetry/processed/ProcessedTelemetryData'
import { GpsPoint } from '../../../domain/telemetry/processed/GpsPoint'
import { LeanAnglePoint } from '../../../domain/telemetry/processed/LeanAngle'
import { AccelerationPoint } from '../../../domain/telemetry/processed/Acceleration'
import { GForcePoint } from '../../../domain/telemetry/processed/GForce'
import { messageOf } from '../../../utils/errorMessage'
import { MS_PER_S } from '../../../utils/timeUnits'

export class TelemetrySynchronizationStage implements PipelineStage {
  name = 'Telemetry Synchronization'

  async execute(context: PipelineContext): Promise<StageResult<void>> {
    const warnings: string[] = []

    try {
      const { processedTelemetry, options } = context

      if (!processedTelemetry) {
        return {
          success: false,
          error: 'No processed telemetry data available',
        }
      }

      // If data is already at high frequency, skip synchronization
      const avgFrequency = processedTelemetry.statistics.samplingFrequencyHz
      const targetFrequency = MS_PER_S / options.syncIntervalMs // Hz

      if (avgFrequency >= targetFrequency * 0.9) {
        // Data is already at or above target frequency
        context.synchronizedTelemetry = processedTelemetry
        return {
          success: true,
          warnings: [
            'Skipped synchronization - data already at target frequency',
          ],
        }
      }

      // Perform synchronization
      const synchronized = this.synchronizeTelemetry(
        processedTelemetry,
        options.syncIntervalMs
      )

      context.synchronizedTelemetry = synchronized

      return {
        success: true,
        warnings: warnings.length > 0 ? warnings : undefined,
      }
    } catch (error) {
      return {
        success: false,
        error: `Telemetry synchronization failed: ${messageOf(error)}`,
      }
    }
  }

  /**
   * Synchronize all telemetry to a fixed time interval
   */
  private synchronizeTelemetry(
    telemetry: ProcessedTelemetryData,
    intervalMs: number
  ): ProcessedTelemetryData {
    const { gpsPoints } = telemetry

    if (gpsPoints.length === 0) {
      return telemetry
    }

    // Determine time range
    const startTime = gpsPoints[0].elapsedMs
    const endTime = gpsPoints[gpsPoints.length - 1].elapsedMs
    const duration = endTime - startTime

    // Calculate number of synchronized points
    const pointCount = Math.floor(duration / intervalMs) + 1

    // Generate synchronized GPS points
    const syncGpsPoints: GpsPoint[] = []
    for (let i = 0; i < pointCount; i++) {
      const targetTime = startTime + i * intervalMs
      const point = this.interpolateGpsPoint(gpsPoints, targetTime)
      if (point) {
        syncGpsPoints.push(point)
      }
    }

    const syncLeanAngle: LeanAnglePoint[] = []
    for (let i = 0; i < pointCount; i++) {
      const targetTime = startTime + i * intervalMs
      const point = this.interpolateLeanAngle(telemetry.leanAngle, targetTime)
      if (point) {
        syncLeanAngle.push(point)
      }
    }

    const syncAcceleration: AccelerationPoint[] = []
    for (let i = 0; i < pointCount; i++) {
      const targetTime = startTime + i * intervalMs
      const point = this.interpolateAcceleration(
        telemetry.acceleration,
        targetTime
      )
      if (point) {
        syncAcceleration.push(point)
      }
    }

    const syncGForce: GForcePoint[] = []
    for (let i = 0; i < pointCount; i++) {
      const targetTime = startTime + i * intervalMs
      const point = this.interpolateGForce(telemetry.gForce, targetTime)
      if (point) {
        syncGForce.push(point)
      }
    }

    return {
      gpsPoints: syncGpsPoints,
      leanAngle: syncLeanAngle,
      acceleration: syncAcceleration,
      gForce: syncGForce,
      statistics: telemetry.statistics,
      diagnostics: telemetry.diagnostics,
    }
  }

  /**
   * Interpolate GPS point at specific time
   */
  private interpolateGpsPoint(
    points: GpsPoint[],
    targetTimeMs: number
  ): GpsPoint | null {
    // Find bracketing points
    const index = this.findBracketingIndex(points, targetTimeMs)
    if (index === null) return null

    // If exact match
    if (points[index].elapsedMs === targetTimeMs) {
      return points[index]
    }

    // If at end
    if (index === points.length - 1) {
      return points[index]
    }

    // Interpolate
    const before = points[index]
    const after = points[index + 1]
    const ratio =
      (targetTimeMs - before.elapsedMs) / (after.elapsedMs - before.elapsedMs)

    return {
      latitude: before.latitude + (after.latitude - before.latitude) * ratio,
      longitude:
        before.longitude + (after.longitude - before.longitude) * ratio,
      altitude: this.interpolateOptionalNumber(
        before.altitude,
        after.altitude,
        ratio
      ),
      speedKmh: before.speedKmh + (after.speedKmh - before.speedKmh) * ratio,
      timestamp: new Date(
        before.timestamp.getTime() +
          (after.timestamp.getTime() - before.timestamp.getTime()) * ratio
      ),
      distanceMeters:
        before.distanceMeters +
        (after.distanceMeters - before.distanceMeters) * ratio,
      elapsedMs: targetTimeMs,
    }
  }

  /**
   * Interpolate lean angle point at specific time
   */
  private interpolateLeanAngle(
    points: LeanAnglePoint[],
    targetTimeMs: number
  ): LeanAnglePoint | null {
    const index = this.findBracketingIndex(points, targetTimeMs)
    if (index === null) return null

    if (points[index].elapsedMs === targetTimeMs) {
      return points[index]
    }

    if (index === points.length - 1) {
      return points[index]
    }

    const before = points[index]
    const after = points[index + 1]
    const ratio =
      (targetTimeMs - before.elapsedMs) / (after.elapsedMs - before.elapsedMs)

    return {
      leanAngleDegrees:
        before.leanAngleDegrees +
        (after.leanAngleDegrees - before.leanAngleDegrees) * ratio,
      timestamp: new Date(
        before.timestamp.getTime() +
          (after.timestamp.getTime() - before.timestamp.getTime()) * ratio
      ),
      distanceMeters:
        before.distanceMeters +
        (after.distanceMeters - before.distanceMeters) * ratio,
      elapsedMs: targetTimeMs,
      speedKmh: before.speedKmh + (after.speedKmh - before.speedKmh) * ratio,
      source: before.source,
    }
  }

  /**
   * Interpolate acceleration point at specific time
   */
  private interpolateAcceleration(
    points: AccelerationPoint[],
    targetTimeMs: number
  ): AccelerationPoint | null {
    const index = this.findBracketingIndex(points, targetTimeMs)
    if (index === null) return null

    if (points[index].elapsedMs === targetTimeMs) {
      return points[index]
    }

    if (index === points.length - 1) {
      return points[index]
    }

    const before = points[index]
    const after = points[index + 1]
    const ratio =
      (targetTimeMs - before.elapsedMs) / (after.elapsedMs - before.elapsedMs)

    return {
      longitudinalMs2:
        before.longitudinalMs2 +
        (after.longitudinalMs2 - before.longitudinalMs2) * ratio,
      lateralMs2:
        before.lateralMs2 + (after.lateralMs2 - before.lateralMs2) * ratio,
      verticalMs2:
        before.verticalMs2 + (after.verticalMs2 - before.verticalMs2) * ratio,
      timestamp: new Date(
        before.timestamp.getTime() +
          (after.timestamp.getTime() - before.timestamp.getTime()) * ratio
      ),
      distanceMeters:
        before.distanceMeters +
        (after.distanceMeters - before.distanceMeters) * ratio,
      elapsedMs: targetTimeMs,
      source: before.source,
      speedTrendMismatch: before.speedTrendMismatch || after.speedTrendMismatch,
    }
  }

  /**
   * Interpolate g-force point at specific time
   */
  private interpolateGForce(
    points: GForcePoint[],
    targetTimeMs: number
  ): GForcePoint | null {
    const index = this.findBracketingIndex(points, targetTimeMs)
    if (index === null) return null

    if (points[index].elapsedMs === targetTimeMs) {
      return points[index]
    }

    if (index === points.length - 1) {
      return points[index]
    }

    const before = points[index]
    const after = points[index + 1]
    const ratio =
      (targetTimeMs - before.elapsedMs) / (after.elapsedMs - before.elapsedMs)

    return {
      longitudinalG:
        before.longitudinalG +
        (after.longitudinalG - before.longitudinalG) * ratio,
      lateralG: before.lateralG + (after.lateralG - before.lateralG) * ratio,
      verticalG:
        before.verticalG + (after.verticalG - before.verticalG) * ratio,
      combinedG:
        before.combinedG + (after.combinedG - before.combinedG) * ratio,
      timestamp: new Date(
        before.timestamp.getTime() +
          (after.timestamp.getTime() - before.timestamp.getTime()) * ratio
      ),
      distanceMeters:
        before.distanceMeters +
        (after.distanceMeters - before.distanceMeters) * ratio,
      elapsedMs: targetTimeMs,
    }
  }

  /**
   * Find index of point at or before target time
   */
  private findBracketingIndex<T extends { elapsedMs: number }>(
    points: T[],
    targetTimeMs: number
  ): number | null {
    if (points.length === 0) return null

    // Binary search for efficiency
    let left = 0
    let right = points.length - 1

    while (left <= right) {
      const mid = Math.floor((left + right) / 2)
      const point = points[mid]

      if (point.elapsedMs === targetTimeMs) {
        return mid
      } else if (point.elapsedMs < targetTimeMs) {
        left = mid + 1
      } else {
        right = mid - 1
      }
    }

    // Return the point before target time
    return right >= 0 ? right : null
  }

  /**
   * Interpolate optional number values
   */
  private interpolateOptionalNumber(
    before: number | undefined,
    after: number | undefined,
    ratio: number
  ): number | undefined {
    if (before === undefined || after === undefined) {
      return before ?? after
    }
    return before + (after - before) * ratio
  }
}
